import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../state/store';
import { previews, overlays, dropOverlay, hasOverlay, putOverlay } from '../state/bitmaps';
import { engine } from '../worker/client';
import { PREVIEW_MAX } from '../worker/protocol';
import { fitViewport, zoomAt, pan, type Viewport } from '../lib/viewport';
import { filmViewport } from '../lib/film-view';
import { hudShowsBackground, hudText } from '../lib/format';
import { HANDLE_DOT_PX, ROTATE_HANDLE_PX, areaOutlines, rotateHandleAt, sameAspect } from '../lib/mockup-view';
import { useSepView } from './useSepView';
import { useMockupView } from './useMockupView';
import { PlateBar } from './PlateBar';

const OVERLAY_COLORS: Record<string, [number, number, number]> = {
  semi: [255, 80, 80], specks: [255, 181, 71], thin: [255, 80, 200], gaps: [80, 200, 255], background: [255, 255, 0], 'despeckle:candidates': [255, 181, 71], 'halftone:halftoned': [120, 255, 120],
};

/** What the canvas is painted on behind the art, named in words the HUD can show
 *  as they are ("garment"/"checker"/"white" were lower-case implementation
 *  values sitting in a row of buttons — audit #20). */
const BACKGROUNDS = [
  { value: 'garment', label: 'Garment' },
  { value: 'checker', label: 'Checker' },
  { value: 'white', label: 'White' },
] as const;

/**
 * What the mockup canvas tells a screen reader, and the whole of how a shop that
 * cannot hold a steady drag places art (audit #12). The keys themselves are handled
 * by `useMockupView.onKeyDown`; this sentence is the only place they are written
 * down for a person.
 */
export const MOCKUP_CANVAS_LABEL = 'Mockup preview. Arrow keys move the print by 0.1 inch, Shift+arrows by 1 inch, [ and ] rotate by 1 degree, Shift for 15 degrees.';

function makeCheckerPattern(ctx: CanvasRenderingContext2D): CanvasPattern {
  const c = document.createElement('canvas'); c.width = c.height = 16;
  const g = c.getContext('2d')!; g.fillStyle = '#2a2e34'; g.fillRect(0, 0, 16, 16); g.fillStyle = '#3a3f47'; g.fillRect(0, 0, 8, 8); g.fillRect(8, 8, 8, 8);
  return ctx.createPattern(c, 'repeat')!;
}

export function Viewer() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  const viewer = useStore((s) => s.viewer);
  const appMode = useStore((s) => s.appMode);
  const setViewer = useStore((s) => s.setViewer);
  const garmentHex = useStore((s) => s.settings.garmentHex);
  const [vp, setVp] = useState<Viewport | null>(null);
  const [overlayVersion, setOverlayVersion] = useState(0);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const checkerRef = useRef<{ ctx: CanvasRenderingContext2D; pattern: CanvasPattern } | null>(null);
  // Called before every effect below so its own effects register FIRST: the
  // film-crop request reads the viewport through a ref that one of them syncs,
  // and the fit effect further down is what tells it the viewport settled.
  const sep = useSepView(doc, canvasRef, vp);
  const mockup = useMockupView(doc, canvasRef, vp);
  const which = viewer.view;
  const dtfPreview = doc ? previews.get(doc.id)?.[which] : undefined;
  // The garment mockup is drawn from its own cached bitmap scaled up to the
  // PHOTO's size — not the bitmap's — because that is the space the print-area
  // outline and the placement drag work in, so it is also what the viewport is
  // fitted to and what the HUD counts pixels in. Memoised on the hook's own
  // (already memoised) entry so the draw effect below sees a stable object.
  const mockupPreview = useMemo(
    () => (mockup.preview
      ? { bitmap: mockup.preview.bitmap, factor: mockup.preview.width / mockup.preview.bitmap.width, width: mockup.preview.width, height: mockup.preview.height }
      : undefined),
    [mockup.preview],
  );
  // Mockup mode with a render actually in hand. Before the first one lands the
  // DTF output stays on canvas, dimmed, with "rendering…" in the HUD — so
  // `mockup.active` alone is not enough to paint from.
  const mockupOnCanvas = mockup.active && !!mockupPreview;
  // The GPU has a frame for exactly the job on canvas: it is drawn instead of the CPU render AND
  // instead of the blank+ghost pair, because it is not an aid — it is the same composite the worker
  // makes, a gesture ahead of it. Two gates besides the frame itself:
  //  - `mockupOnCanvas`, because before the first CPU render lands it is the DTF output that is on
  //    screen, in the ART's coordinate space rather than the photo's, and a GPU frame scaled into
  //    that would be nonsense;
  //  - the same SHAPE as the render on canvas, because both are drawn stretched to the photo's
  //    full-res size. They agree always but for one window: the job's template changing to one with
  //    a different photo aspect whose maps are already cached, where the GPU is a template ahead of
  //    the canvas and drawing it would visibly distort the garment until the new render lands.
  const glOnCanvas = mockupOnCanvas && !!mockup.glFrame && !!mockupPreview && sameAspect(mockup.glFrame, mockupPreview);
  // In screen-print mode the canvas shows the separation instead: the composite
  // proof, one plate, or (handled separately below) that plate's film at 1:1.
  const preview = sep.active ? sep.preview : mockupOnCanvas ? mockupPreview : dtfPreview;
  // What the viewport is fitted to and what a double-click refits to: the whole
  // film in film view, otherwise whichever preview is on screen.
  const imgW = sep.filmActive ? sep.filmW : preview?.width;
  const imgH = sep.filmActive ? sep.filmH : preview?.height;
  const previewVersion = doc?.previewVersion ?? 0;
  const inspectVersion = doc?.inspectVersion ?? 0;
  // A check overlay bitmap is only valid for one (output, inspection) pair: a run
  // replaces the output (previewVersion) and every inspection rebuilds every check
  // mask from the new preset (inspectVersion), including dropping the mask entirely
  // when the check now passes.
  const overlayEpoch = `${previewVersion}:${inspectVersion}`;
  // stable, order-independent key of the checks whose overlay the worker can serve right now
  const overlayReady = doc ? doc.checks.filter((c) => c.hasOverlay).map((c) => c.id).sort().join(',') : '';
  // A check that reports no overlay this time round must stop painting the last
  // one (e.g. Supacolor's lower minLinePx makes thin-lines pass with no mask).
  // Step overlays ('despeckle:candidates') are not checks and are unaffected.
  const hasNoOverlay = (key: string) => doc?.checks.some((c) => c.id === key && !c.hasOverlay) ?? false;
  // Step overlays ('despeckle:candidates') are rebuilt only by a run, never by an
  // inspection, so keying them on the full overlayEpoch (which also moves on an
  // inspect-only pass) would miss the cache and refetch a bitmap the worker never
  // changed. Key them on previewVersion alone; everything else keeps the full epoch.
  const epochFor = (key: string) => (key.includes(':') ? String(previewVersion) : overlayEpoch);

  function checker(ctx: CanvasRenderingContext2D): CanvasPattern {
    if (checkerRef.current && checkerRef.current.ctx === ctx) return checkerRef.current.pattern;
    const pattern = makeCheckerPattern(ctx);
    checkerRef.current = { ctx, pattern };
    return pattern;
  }

  // Overlay bitmaps are fetched once per (doc, overlayEpoch, overlay key).
  //
  // Three things make this subtle. (a) A completed run replaces the worker's
  // output overlay map with the step overlays, and the check overlays are only
  // re-added as inspect() reaches each check — so a fetch issued the moment the
  // run lands comes back null and nothing is cached. `overlayReady` is a stable
  // string of the check ids that currently HAVE an overlay, so the effect
  // refires exactly when a needed overlay becomes available, and the cache
  // short-circuit below makes the extra passes free. (b) The run generation is
  // deliberately NOT a dependency: it changes when the run lands, which is one
  // render BEFORE the new preview, so keying on it fetched every overlay twice
  // per run (the first fetch against the old previewVersion was immediately
  // evicted by the second). `previewVersion` always bumps once the preview for
  // that run has landed, which is also when `dtfPreview` below becomes non-null,
  // so the fetch happens once, after the preview. (c) `inspectVersion` joins it
  // in `overlayEpoch` because an inspect-only pass (a vendor-preset change)
  // rebuilds every check mask without touching the output or the preview; keyed
  // on `previewVersion` alone, every fetch short-circuited on the cache and the
  // canvas kept painting the previous preset's mask. `previewVersion` is
  // deliberately NOT bumped for an inspect-only pass — the full-res output is
  // unchanged, so re-downsampling it would be pure waste.
  //
  // Skipped entirely while a separation view is on canvas: the overlays are
  // masks over the DTF output, which is not what is being painted. Mockup mode
  // skips them for the same reason — and keeps skipping them while the DTF
  // output stands in for a render still in flight, since a mask painted over
  // that stand-in would read as part of the mockup.
  useEffect(() => {
    if (!doc || !dtfPreview || sep.active || mockup.active) return;
    let cancelled = false;
    (async () => {
      for (const [key, on] of Object.entries(viewer.overlays)) {
        if (!on) continue;
        // A check that no longer reports a mask must stop painting the last one
        // instead of leaving it cached until the doc closes.
        // `hasOverlay` first: this runs for every enabled key on every pass, and
        // `dropOverlay` would otherwise walk (and copy) the whole cache key list
        // for keys that were never cached.
        if (hasNoOverlay(key)) { if (hasOverlay(doc.id, which, key)) dropOverlay(doc.id, which, key); continue; }
        const epoch = epochFor(key);
        const cacheKey = `${doc.id}:${which}:${key}:${epoch}`;
        if (overlays.has(cacheKey)) continue;
        const bmp = await engine().overlay(doc.id, which, key, PREVIEW_MAX, OVERLAY_COLORS[key] ?? [255, 255, 255]);
        if (cancelled) { bmp?.close(); return; }
        if (bmp) { putOverlay(doc.id, which, key, epoch, bmp); setOverlayVersion((v) => v + 1); }
      }
    })();
    return () => { cancelled = true; };
  }, [doc?.id, overlayEpoch, overlayReady, which, viewer.overlays, sep.active, mockup.active]);

  // The viewport a fresh view opens at, and what a double-click or a canvas
  // resize returns to. The film view opens at 100 % centred — see `filmViewport`
  // — everything else is fitted to the canvas.
  function reset(c: HTMLCanvasElement, w: number, h: number): Viewport {
    return sep.filmActive ? filmViewport(w, h, c.clientWidth, c.clientHeight) : fitViewport(w, h, c.clientWidth, c.clientHeight);
  }

  // fit when the document or the image behind the viewport changes (a new
  // preview size, entering the film view, or leaving it again)
  useEffect(() => {
    const c = canvasRef.current; if (!c || !imgW || !imgH) { setVp(null); return; }
    setVp(reset(c, imgW, imgH));
    // The film view's crop is chosen from the viewport, so the request waits
    // until the viewport it will be read from actually exists.
    sep.settle();
  }, [doc?.id, imgW, imgH, which, sep.filmActive, sep.settle]);

  // draw
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth, h = c.clientHeight;
    if (c.width !== w * dpr || c.height !== h * dpr) { c.width = w * dpr; c.height = h * dpr; }
    const ctx = c.getContext('2d')!;
    // Republished at the end of every draw that has a box. Cleared first so the
    // early returns below (the film view, nothing to draw) cannot leave the last
    // mode's box behind.
    delete c.dataset.printBox;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#15171a'; ctx.fillRect(0, 0, w, h);
    // The 1:1 film: the viewport's "image" is the whole film, of which the
    // worker only ever hands over the visible window. The rest is bare film —
    // white paper, with the ink black on top of it.
    if (sep.filmActive && sep.filmW && sep.filmH && vp) {
      ctx.save();
      ctx.translate(vp.tx, vp.ty); ctx.scale(vp.scale, vp.scale);
      ctx.imageSmoothingEnabled = vp.scale * dpr < 1;
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, sep.filmW, sep.filmH);
      if (sep.filmBitmap && sep.filmRect) ctx.drawImage(sep.filmBitmap, sep.filmRect.x, sep.filmRect.y);
      ctx.restore();
      return;
    }
    if (!preview || !vp) return;
    ctx.save();
    ctx.translate(vp.tx, vp.ty); ctx.scale(vp.scale, vp.scale);
    // Nearest-neighbour once a source pixel covers at least one DEVICE pixel, not
    // one CSS pixel: on a 2x display, a CSS zoom of 0.5 is already 1 device pixel
    // per source pixel and should be crisp, which `vp.scale * preview.factor`
    // alone (no dpr) would miss.
    ctx.imageSmoothingEnabled = vp.scale * preview.factor * dpr < 1;
    // background behind the art
    if (viewer.background === 'checker') { ctx.fillStyle = checker(ctx); ctx.fillRect(0, 0, preview.width, preview.height); }
    else { ctx.fillStyle = viewer.background === 'garment' ? garmentHex : '#ffffff'; ctx.fillRect(0, 0, preview.width, preview.height); }
    // A ghost drag paints the art-free garment (rendered once, cached) with the
    // art laid over it, instead of a render that is a gesture out of date.
    if (glOnCanvas && mockup.glFrame) ctx.drawImage(mockup.glFrame, 0, 0, preview.width, preview.height);
    else if (mockup.ghosting && mockup.blank) ctx.drawImage(mockup.blank.bitmap, 0, 0, mockup.blank.width, mockup.blank.height);
    else ctx.drawImage(preview.bitmap, 0, 0, preview.width, preview.height);
    // The art the pointer is moving, through the affine map that takes the art
    // bitmap's own pixel space onto the box's tl/tr/bl corners. `transform`, not
    // `setTransform`: the viewport is already on the context and the ghost must
    // not throw it away. A keystoned box gets the affine APPROXIMATION of its
    // homography — this is a placement aid, and the render that lands a moment
    // later is the one that keystones properly.
    //
    // Clipped to the print-area quad, because that is what the compositor does:
    // art dragged past the edge is cropped there, and a ghost that floated on
    // over the backdrop would promise a mockup the render will not deliver.
    // Never over a GPU frame: that frame already HAS the art on it, properly keystoned and shaded,
    // and a flat copy laid on top would be the only wrong thing on the canvas.
    if (!glOnCanvas && mockup.ghosting && mockup.ghostArt && mockup.corners && mockup.template) {
      const [tl, tr, , bl] = mockup.corners;
      const g = mockup.ghostArt;
      if (g.width > 0 && g.height > 0) {
        const q = mockup.template.printArea.quad;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(q.tl[0], q.tl[1]); ctx.lineTo(q.tr[0], q.tr[1]); ctx.lineTo(q.br[0], q.br[1]); ctx.lineTo(q.bl[0], q.bl[1]);
        ctx.closePath();
        ctx.clip();
        ctx.transform((tr[0] - tl[0]) / g.width, (tr[1] - tl[1]) / g.width, (bl[0] - tl[0]) / g.height, (bl[1] - tl[1]) / g.height, tl[0], tl[1]);
        ctx.drawImage(g, 0, 0);
        ctx.restore();
      }
    }
    // Check overlays belong to the DTF output; a plate, a proof or a mockup is a
    // different image at a different size, so they are not painted over it.
    if (!sep.active && !mockup.active) {
      for (const [key, on] of Object.entries(viewer.overlays)) {
        if (!on || !doc || hasNoOverlay(key)) continue;
        const bmp = overlays.get(`${doc.id}:${which}:${key}:${epochFor(key)}`);
        if (bmp) ctx.drawImage(bmp, 0, 0, preview.width, preview.height);
      }
    }
    // The mockup's first render is still compositing and the DTF output is
    // standing in for it: dimmed, so it cannot be read as the finished mockup.
    if (mockup.active && !mockupOnCanvas) { ctx.fillStyle = 'rgba(21,23,26,.55)'; ctx.fillRect(0, 0, preview.width, preview.height); }
    // Where a drag may place the art — and, on a blank with more than one area, where the OTHER
    // prints can go. One SCREEN pixel wide (and dashed in screen pixels) at every zoom, so it
    // stays a hairline over a 3000 px photo instead of swelling into a band; `restore()` below
    // puts the dash back. The primary area keeps the cyan it always had — it is the one the
    // transform box belongs to — and the rest are grey and NAMED, since with several outlines up
    // "which one is the sleeve?" cannot be answered by counting quads.
    if (mockupOnCanvas && viewer.showPrintArea && mockup.template) {
      ctx.lineWidth = 1 / vp.scale;
      for (const a of areaOutlines(mockup.template)) {
        const q = a.quad;
        ctx.setLineDash([6 / vp.scale, 4 / vp.scale]);
        ctx.strokeStyle = a.primary ? 'rgba(0,255,255,0.7)' : 'rgba(198,205,215,0.55)';
        ctx.beginPath();
        ctx.moveTo(q.tl[0], q.tl[1]); ctx.lineTo(q.tr[0], q.tr[1]); ctx.lineTo(q.br[0], q.br[1]); ctx.lineTo(q.bl[0], q.bl[1]);
        ctx.closePath();
        ctx.stroke();
        if (!a.primary && a.name) {
          // 12 SCREEN px at every zoom, like the dashes, and just above the area's top-left
          // corner so it labels the outline without sitting on the garment inside it.
          ctx.setLineDash([]);
          ctx.fillStyle = 'rgba(198,205,215,0.9)';
          ctx.font = `${12 / vp.scale}px system-ui, sans-serif`;
          ctx.textBaseline = 'bottom';
          ctx.fillText(a.name, q.tl[0], q.tl[1] - 3 / vp.scale);
        }
      }
    }
    // Each ENABLED extra print's own art rectangle. Thin, grey and solid: a readout of where the
    // panel's numbers put it, not something the pointer can grab — only the primary print has a
    // transform box in this task.
    if (mockupOnCanvas && mockup.extraBoxes.length) {
      ctx.setLineDash([]);
      ctx.lineWidth = 1 / vp.scale;
      ctx.strokeStyle = 'rgba(198,205,215,0.85)';
      for (const [tl, tr, br, bl] of mockup.extraBoxes) {
        ctx.beginPath();
        ctx.moveTo(tl[0], tl[1]); ctx.lineTo(tr[0], tr[1]); ctx.lineTo(br[0], br[1]); ctx.lineTo(bl[0], bl[1]);
        ctx.closePath();
        ctx.stroke();
      }
    }
    // The transform box: where the art actually is, and the four corners a drag
    // resizes it by. Solid, and sized in SCREEN pixels at every zoom like the
    // dashed outline above — `setLineDash([])` because that outline left a dash
    // on the context.
    if (mockupOnCanvas && mockup.corners) {
      const [tl, tr, br, bl] = mockup.corners;
      ctx.setLineDash([]);
      ctx.lineWidth = 1 / vp.scale;
      ctx.strokeStyle = '#00e5ff';
      ctx.beginPath();
      ctx.moveTo(tl[0], tl[1]); ctx.lineTo(tr[0], tr[1]); ctx.lineTo(br[0], br[1]); ctx.lineTo(bl[0], bl[1]);
      ctx.closePath();
      ctx.stroke();
      // The rotate handle, on a 1-px stem off the middle of the box's own top edge — so it turns
      // with the box and stays clear of the two top corner handles at every zoom.
      const rot = rotateHandleAt(mockup.corners, ROTATE_HANDLE_PX / vp.scale);
      if (rot) {
        ctx.beginPath();
        ctx.moveTo((tl[0] + tr[0]) / 2, (tl[1] + tr[1]) / 2);
        ctx.lineTo(rot[0], rot[1]);
        ctx.stroke();
      }
      const r = HANDLE_DOT_PX / vp.scale;
      ctx.fillStyle = '#00e5ff';
      ctx.strokeStyle = '#0c0f13';
      for (const p of rot ? [...mockup.corners, rot] : mockup.corners) { ctx.beginPath(); ctx.arc(p[0], p[1], r, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); }
    }
    ctx.restore();
    // The box in the canvas's own CSS pixels. It exists nowhere but on the
    // canvas, so it is published where a test (and anything else outside the
    // 2d context) can read it. It is the AXIS-ALIGNED BOUNDING BOX of the four
    // corners: for the flat blanks (an axis-aligned print area) that is the box
    // itself, and for a keystoned one it is its envelope.
    if (mockupOnCanvas && mockup.corners) {
      const xs = mockup.corners.map((p) => vp.tx + p[0] * vp.scale), ys = mockup.corners.map((p) => vp.ty + p[1] * vp.scale);
      const x0 = Math.min(...xs), y0 = Math.min(...ys);
      c.dataset.printBox = `${Math.round(x0)},${Math.round(y0)},${Math.round(Math.max(...xs) - x0)},${Math.round(Math.max(...ys) - y0)}`;
    }
  }, [preview, vp, viewer.background, viewer.overlays, viewer.showPrintArea, garmentHex, overlayVersion, overlayEpoch, overlayReady,
    sep.active, sep.filmActive, sep.filmW, sep.filmH, sep.filmRect, sep.filmBitmap,
    mockup.active, mockupOnCanvas, mockup.template, mockup.corners, mockup.extraBoxes, mockup.ghosting, mockup.blank, mockup.ghostArt, mockup.frame,
    // The canvas object is the singleton renderer's and never changes identity, so `glVersion` — one
    // bump per GPU frame rendered — is what says its pixels moved. Without it a panel edit would
    // render a new GPU frame that nothing repainted onto the canvas until the CPU render landed.
    glOnCanvas, mockup.glFrame, mockup.glVersion]);

  // resize observer → refit, but only when the canvas's own size actually changes. This used to
  // re-subscribe on every new `preview` object as well — `observe` delivers an initial
  // observation, so a run replacing the preview at the same size refitted the viewport — which in
  // Mockup mode meant EVERY placement edit (a new render, a new preview object) threw a zoomed-in
  // view back out to fit (hands-on, 2026-09-29). A size change of the image itself is the fit
  // effect above's job; a same-size replacement is nobody's.
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    let lastW = c.clientWidth, lastH = c.clientHeight;
    const ro = new ResizeObserver(() => {
      if (!imgW || !imgH) return;
      const w = c.clientWidth, h = c.clientHeight;
      // The initial observation `observe` delivers, or a notification without a layout change.
      if (w === lastW && h === lastH) return;
      lastW = w; lastH = h;
      setVp(reset(c, imgW, imgH));
      sep.settle();
    });
    ro.observe(c); return () => ro.disconnect();
  }, [imgW, imgH, sep.filmActive, sep.settle]);

  // wheel-zoom: React's onWheel is delegated as a passive root listener, so calling
  // preventDefault() from a JSX handler logs "Unable to preventDefault inside passive
  // event listener invocation." Attach a native, non-passive listener instead.
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const onCanvasWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = c.getBoundingClientRect();
      const cx = e.clientX - r.left, cy = e.clientY - r.top;
      setVp((prev) => (prev ? zoomAt(prev, Math.exp(-e.deltaY * 0.0015), cx, cy) : prev));
      // A no-op outside the film view; inside it, re-requests the window once
      // the gesture has stopped.
      sep.wheelSettle();
    };
    c.addEventListener('wheel', onCanvasWheel, { passive: false });
    return () => c.removeEventListener('wheel', onCanvasWheel);
  }, [sep.wheelSettle]);

  // The mockup's HUD terms: the blank it is rendered on, then whatever is true
  // of what is on screen. `clipped` is only reported for a render actually in
  // hand — the flag belongs to the last one, and reporting it over the dimmed
  // stand-in would describe an image nobody can see.
  const mockupStatus = mockup.active
    ? [
        mockup.template?.name ?? '',
        mockup.rendering ? 'rendering…' : '',
        mockup.stale ? 'stale' : '',
        // The angle the box on canvas is drawn at — a drag's live one while a gesture is in hand,
        // so the readout turns with the art rather than a render behind it.
        mockupOnCanvas && mockup.rotationDeg ? `rotated ${mockup.rotationDeg}°` : '',
        mockupOnCanvas && mockup.clipped ? 'clipped' : '',
        // Extra prints the last render could not put anywhere: an area this blank does not have,
        // or a document that has been closed. The inspector names them.
        mockupOnCanvas && mockup.skipped.length ? `${mockup.skipped.length} skipped` : '',
      ].filter(Boolean).join(' · ')
    : '';
  const readout = hudText({
    size: sep.filmActive && sep.filmW && sep.filmH
      ? { width: sep.filmW, height: sep.filmH }
      : preview ? { width: preview.width, height: preview.height } : null,
    // A mockup is measured in the photo's own pixels, like a film is in the film's.
    unit: sep.filmActive || mockupOnCanvas ? 'px' : undefined,
    open: !!doc,
    running: !!doc?.running,
    zoom: vp ? vp.scale : null,
    status: sep.status || mockupStatus || undefined,
  });

  return (
    <div className="viewer-wrap">
      {/* A press inside the mockup's print area places the art and consumes the
          gesture; everywhere else — and in every other mode, where the mockup
          handlers return false on their first line — this is the pan it always was. */}
      <canvas ref={canvasRef} className="viewer-canvas"
        // Focusable, named, and keyboard-driven — but only where there is a print
        // to move: in DTF and screen-print mode the canvas is a picture nothing
        // can be done to, and a focus stop there would be a tab press that
        // announces nothing and does nothing.
        tabIndex={mockup.active ? 0 : undefined}
        // `application`, not `img`: an `img` role announces a static graphic, so a
        // screen reader keeps its virtual cursor on it and swallows the arrow keys
        // the label promises until the user knows to enter focus mode. `application`
        // is the one role that tells assistive tech the widget takes the keys itself.
        role={mockup.active ? 'application' : undefined}
        aria-label={mockup.active ? MOCKUP_CANVAS_LABEL : undefined}
        onKeyDown={(e) => { mockup.onKeyDown(e); }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          if (mockup.onPointerDown(e, vp)) return;
          drag.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerMove={(e) => {
          if (mockup.onPointerMove(e, vp)) return;
          if (!drag.current || !vp) return; setVp(pan(vp, e.clientX - drag.current.x, e.clientY - drag.current.y)); drag.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={(e) => { mockup.onPointerUp(e, vp); drag.current = null; sep.settle(); }}
        onPointerCancel={(e) => { mockup.onPointerCancel(e); drag.current = null; sep.settle(); }}
        onDoubleClick={(e) => { if (!imgW || !imgH) return; setVp(reset(e.currentTarget, imgW, imgH)); sep.settle(); }}
      />
      <div className="viewer-hud">
        <div className="row">
          {sep.show ? (
            <PlateBar plates={sep.plates} plate={sep.plate} filmView={sep.filmActive}
              onPlate={(next) => setViewer({ plate: next, filmView: next === 'proof' ? false : sep.filmActive })}
              onFilmView={(next) => setViewer({ filmView: next })} />
          ) : mockup.active ? (
            <>
              {/* Only with a back design bound and its blank prepared: with
                  nothing to switch to, a disabled pair of buttons would say
                  less than no buttons at all. */}
              {mockup.canToggleView && (['front', 'back'] as const).map((side) => (
                <button key={side} className={mockup.view === side ? 'primary' : ''} aria-pressed={mockup.view === side}
                  onClick={() => mockup.setView(side)}>
                  {side === 'front' ? 'Front' : 'Back'}
                </button>
              ))}
              <button className={viewer.showPrintArea ? 'primary' : ''} aria-pressed={viewer.showPrintArea}
                title="Outline the area the art can be placed in"
                onClick={() => setViewer({ showPrintArea: !viewer.showPrintArea })}>
                Print area
              </button>
            </>
          ) : (
            <>
              {/* Audit #20: two pairs of bare words sat side by side in this bar
                  with nothing to say which question either answered. Each pair is
                  a labelled group now, and every button says whether it is on. */}
              <span className="muted small">View:</span>
              <button className={which === 'output' ? 'primary' : ''} aria-pressed={which === 'output'} onClick={() => setViewer({ view: 'output' })}>Output</button>
              <button className={which === 'source' ? 'primary' : ''} aria-pressed={which === 'source'} onClick={() => setViewer({ view: 'source' })}>Source</button>
            </>
          )}
          {/* Not in Mockup mode: see `hudShowsBackground`. The separator goes with it —
              there is nothing on its right to separate. */}
          {hudShowsBackground(appMode) && (
            <>
              <span className="muted small">|</span>
              <span className="muted small">Background:</span>
              {BACKGROUNDS.map((b) => (
                <button
                  key={b.value} className={viewer.background === b.value ? 'primary' : ''} aria-pressed={viewer.background === b.value}
                  onClick={() => setViewer({ background: b.value })}
                >
                  {b.label}
                </button>
              ))}
            </>
          )}
          <span className="muted small right">{readout}</span>
        </div>
      </div>
      {!doc && <div className="viewer-empty">Drop a design here or use Open files</div>}
    </div>
  );
}
